import * as React from "react";
import { useNavigate } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SignupForm } from "@/ui/components/SignupForm";
import { useSignupViewModel } from "@/viewModels/useSignupViewModel";
import { useAuthViewModel } from "@/viewModels/useAuthViewModel";
import { signupFormSchema, type SignupFormData } from "@/schemas/signupSchemas";

/**
 * SignupPage Component
 *
 * Pure view component for user signup.
 * All business logic is in useSignupViewModel.
 *
 * Features:
 * - Email/password/username signup
 * - Social login (Google/Apple)
 * - Unified GDPR consent (single checkbox for Terms, Privacy, Marketing)
 * - Form validation with Zod
 * - Navigate to /verify on successful signup
 * - Navigate to /login on "Sign in" link click
 */
import { AuthLayout } from "@/ui/layouts";

export default function SignupPage() {
  const { t } = useTranslation("auth");
  const { state, actions, computed, onMount } = useSignupViewModel();
  const authViewModel = useAuthViewModel();
  const navigate = useNavigate();

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupFormSchema),
    mode: "onChange",
    defaultValues: {
      gdprConsent: false,
    },
  });

  // Only allowed useEffect - for mount lifecycle
  React.useEffect(onMount, []);

  // Navigate to verify page when status changes to 'verifying'
  React.useEffect(() => {
    if (computed.showVerification) {
      navigate("/auth/verify");
    }
  }, [computed.showVerification, navigate]);

  // Form submission handler
  const onSubmit = handleSubmit((data) => {
    actions.signUp(data);
  });

  // Social signup handlers (OAuth signup = OAuth signin - creates account on first use)
  const handleGoogleSignIn = () => {
    authViewModel.actions.socialLogin("google");
  };

  const handleAppleSignIn = () => {
    authViewModel.actions.socialLogin("apple");
  };

  // Navigate to login page
  const handleSignIn = () => {
    actions.navigateToLogin();
  };

  // Convert form errors to SignupForm field errors and translate them
  const fieldErrors = errors
    ? (Object.entries({
        email: errors.email?.message,
        password: errors.password?.message,
        confirmPassword: errors.confirmPassword?.message,
        username: errors.username?.message,
        gdprConsent: errors.gdprConsent?.message,
      }).reduce((acc, [key, value]) => {
        if (value) {
          // Translate the error message (value is a translation key)
          acc[key as keyof typeof acc] = t(value);
        }
        return acc;
      }, {} as Record<string, string>) as Record<
        "email" | "password" | "confirmPassword" | "username" | "gdprConsent",
        string
      >)
    : undefined;

  return (
    <AuthLayout>
      <SignupForm
        onGoogleSignIn={handleGoogleSignIn}
        onAppleSignIn={handleAppleSignIn}
        onSignUp={(data) => {
          // Set form values for validation
          setValue("email", data.email);
          setValue("password", data.password);
          setValue("confirmPassword", data.confirmPassword);
          setValue("username", data.username);
          setValue("gdprConsent", data.gdprConsent);
          // Trigger form submission with validation
          onSubmit();
        }}
        onSignIn={handleSignIn}
        googleLoading={authViewModel.state.googleLoading}
        appleLoading={authViewModel.state.appleLoading}
        signUpLoading={state.isSubmitting}
        error={computed.hasError ? computed.errorMessage : null}
        fieldErrors={fieldErrors}
      />
    </AuthLayout>
  );
}
